import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { backendClient } from '@/lib/backendClient';
import { Upload, Plus, Trash2 } from 'lucide-react';
import { categories } from '@/config/categories';

interface SessionData {
  category_id: string;
  title: string;
  content: string;
  tags: string[];
  duration: number;
  audioFile: File | null;
}

const SampleSessionUploader: React.FC = () => {
  const [sessionData, setSessionData] = useState<SessionData>({
    category_id: '',
    title: '',
    content: '',
    tags: [],
    duration: 0,
    audioFile: null
  });
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleAddTag = () => {
    if (tagInput.trim() && !sessionData.tags.includes(tagInput.trim())) {
      setSessionData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setSessionData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      setSessionData(prev => ({ ...prev, audioFile: file }));
    } else {
      toast({
        title: "Invalid File",
        description: "Please select an audio file",
        variant: "destructive"
      });
    }
  };

  const uploadSession = async () => {
    if (!sessionData.audioFile || !sessionData.category_id || !sessionData.title || !sessionData.content) {
      toast({
        title: "Missing Information",
        description: "Please fill all required fields and select an audio file",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      // Upload audio file to storage
      const fileName = `sample_${sessionData.category_id}_${Date.now()}.mp3`;
      const { data: uploadData, error: uploadError } = await backendClient.storage
        .from('audio-sessions')
        .upload(`samples/${fileName}`, sessionData.audioFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = backendClient.storage
        .from('audio-sessions')
        .getPublicUrl(`samples/${fileName}`);

      // Create sample session record
      const { error: insertError } = await backendClient
        .from('sample_sessions')
        .insert({
          category_id: sessionData.category_id,
          title: sessionData.title,
          content: sessionData.content,
          tags: sessionData.tags,
          duration: sessionData.duration,
          audio_url: publicUrl
        });

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: "Sample session uploaded successfully"
      });

      // Reset form
      setSessionData({
        category_id: '',
        title: '',
        content: '',
        tags: [],
        duration: 0,
        audioFile: null
      });
      setTagInput('');

    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload sample session",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload Sample Session
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Category Selection */}
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <Select
            value={sessionData.category_id}
            onValueChange={(value) => setSessionData(prev => ({ ...prev, category_id: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={sessionData.title}
            onChange={(e) => setSessionData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Enter session title"
          />
        </div>

        {/* Content */}
        <div className="space-y-2">
          <Label htmlFor="content">Content/Script *</Label>
          <Textarea
            id="content"
            value={sessionData.content}
            onChange={(e) => setSessionData(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Enter the meditation script or content"
            className="min-h-32"
          />
        </div>

        {/* Duration */}
        <div className="space-y-2">
          <Label htmlFor="duration">Duration (seconds) *</Label>
          <Input
            id="duration"
            type="number"
            value={sessionData.duration}
            onChange={(e) => setSessionData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
            placeholder="Duration in seconds"
          />
        </div>

        {/* Tags */}
        <div className="space-y-2">
          <Label>Tags</Label>
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add a tag"
              onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
            />
            <Button onClick={handleAddTag} variant="outline" size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {sessionData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {sessionData.tags.map((tag, index) => (
                <div key={index} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md text-sm">
                  {tag}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Audio File */}
        <div className="space-y-2">
          <Label htmlFor="audio">Audio File *</Label>
          <Input
            id="audio"
            type="file"
            accept="audio/*"
            onChange={handleFileChange}
          />
          {sessionData.audioFile && (
            <p className="text-sm text-muted-foreground">
              Selected: {sessionData.audioFile.name}
            </p>
          )}
        </div>

        {/* Upload Button */}
        <Button
          onClick={uploadSession}
          disabled={uploading}
          className="w-full"
        >
          {uploading ? 'Uploading...' : 'Upload Sample Session'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SampleSessionUploader;
